/**
 * Border Canadian customs PDF modal form.
 * Field order follows docgen/templates/border-canadian/base.hbs.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */

import { OutputTemplateDocumentType } from "@prisma/client";

import { CountrySelect } from "~/components/_shared/inputs/CountrySelect";
import { DocumentAttributePrefillField } from "~/components/domain/settings/documents/DocumentAttributePrefillField";
import { Form } from "~/components/Form";
import {
  type BorderCanadianDocumentInput,
  BorderCanadianDocumentInputSchema,
  borderCanadianDocumentFormInitialValues,
} from "~/modules/orders/order-documents/border-canada/schema";
import { DocumentTypedSignatureModalField } from "~/modules/orders/order-documents/DocumentTypedSignatureModalField";
import { FieldWithPrefillHint } from "~/modules/orders/order-documents/FieldWithPrefillHint";
import { borderCanadaPlannedPrefillHints } from "~/modules/orders/order-documents/order-document-sfdc-field-bindings";
import { PrintSourceRadio } from "~/modules/orders/order-documents/PrintSourceRadio";
import { useBolDocumentAddressLabels } from "~/modules/orders/order-documents/useBolDocumentAddressLabels";
import { DocumentAddressOverrideEditor } from "~/modules/orders/order-menu-document-extensions/DocumentAddressOverrideEditor";
import { OrderDocumentSavedFacts } from "~/modules/orders/order-menu-document-extensions/OrderDocumentSavedFacts";
import type { OrderDocumentFormProps } from "~/modules/orders/order-menu-document-extensions/types";
import { useOptInOrderDocumentForm } from "~/modules/orders/order-menu-document-extensions/useOptInOrderDocumentForm";
import { OPT_IN_ORDER_DOCUMENT_SUBMIT_LABEL, ORDER_DOCUMENT_SLUG } from "~/shared/documents/order-document-settings";
import { DOCUMENT_ATTRIBUTE_WIRE_KEYS } from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { Divider, Stack, Text, Textarea, TextInput } from "~/ui";
import { api } from "~/utils/api";

export const BorderCanadianDocumentForm = ({ orderId, sourceType = "order", onSuccess, onCancel }: OrderDocumentFormProps) => {
  const modalSettingsQuery = api.org.getOrderDocumentDisplayUomForCreateModal.useQuery({ slug: ORDER_DOCUMENT_SLUG.borderCanada });
  const { borderCanadaModalFieldLabels, useConsignorConsigneeLabels } = useBolDocumentAddressLabels();
  const {
    form,
    prefillQuery,
    mutation,
    handleSubmit,
    prefillFeedback,
    isPrefillReady,
    attributePrefillCandidates,
    addressCandidates,
    savedFacts,
    useOutputTemplate,
    setUseOutputTemplate,
  } = useOptInOrderDocumentForm<BorderCanadianDocumentInput>({
    orderId,
    sourceType,
    slug: ORDER_DOCUMENT_SLUG.borderCanada,
    schema: BorderCanadianDocumentInputSchema,
    onSuccess,
    initialValues: { ...borderCanadianDocumentFormInitialValues(), orderId, sourceType },
  });
  const wireKeys = DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada;

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitText={OPT_IN_ORDER_DOCUMENT_SUBMIT_LABEL}
      loading={prefillQuery.isFetching || mutation.isPending}
      feedback={prefillFeedback}
      mutationError={mutation.error}
      disabled={!isPrefillReady}
    >
      <Stack spacing="sm">
        {sourceType === "order" && (
          <PrintSourceRadio
            documentType={OutputTemplateDocumentType.BORDER_CANADA}
            value={useOutputTemplate}
            onChange={setUseOutputTemplate}
            legacyOptionsNote
          />
        )}
        {!useOutputTemplate && (
          <>
            <OrderDocumentSavedFacts
              facts={[
                { label: "Approximate crossing", value: form.values.approximateCrossingDate },
                { label: "Border crossing point", value: form.values.exportBorderCrossing },
                { label: "Port of entry", value: form.values.importCrossingLocation },
                { label: "Freight method", value: savedFacts?.freightMethod ?? form.values.containerType },
              ]}
            />
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.billTo}>
              <DocumentAddressOverrideEditor
                label="Bill To"
                value={form.values.addressOverrides?.billTo}
                onChange={value => form.setFieldValue("addressOverrides.billTo", value)}
                error={form.errors.billTo ?? form.errors["addressOverrides.billTo"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.exporterLegalName}>
              <TextInput
                label="Exporter (legal name)"
                withAsterisk={sourceType === "purchaseOrder"}
                {...form.getInputProps("exporterLegalName")}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.countryOfOrigin}>
              <CountrySelect label="Country of origin" clearable={false} {...form.getInputProps("countryOfOrigin")} />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.vendorAddress}>
              <DocumentAddressOverrideEditor
                label="Supplier"
                value={form.values.addressOverrides?.vendor}
                onChange={value => form.setFieldValue("addressOverrides.vendor", value)}
                error={form.errors.vendorName ?? form.errors.vendorAddress ?? form.errors["addressOverrides.vendor"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.shipFromAddress}>
              <DocumentAddressOverrideEditor
                label={useConsignorConsigneeLabels ? "Consignor" : "Ship from"}
                withAsterisk={sourceType === "purchaseOrder"}
                sourceLabel="Ship From address source"
                candidates={addressCandidates}
                value={form.values.addressOverrides?.shipFrom}
                onChange={value => form.setFieldValue("addressOverrides.shipFrom", value)}
                error={form.errors.shipFromName ?? form.errors.shipFromAddress ?? form.errors["addressOverrides.shipFrom"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.deliverTo}>
              <DocumentAddressOverrideEditor
                label={borderCanadaModalFieldLabels.deliverTo}
                withAsterisk={sourceType === "purchaseOrder"}
                sourceLabel="Delivery address source"
                candidates={addressCandidates}
                preferredCandidateId="delivery"
                value={form.values.addressOverrides?.deliverTo}
                onChange={value => form.setFieldValue("addressOverrides.deliverTo", value)}
                error={form.errors.deliverTo ?? form.errors["addressOverrides.deliverTo"]}
              />
            </FieldWithPrefillHint>
            <Divider label="Line items" />
            {form.values.lineItems.map((line, index) => (
              <Stack key={line.orderItemId} spacing="xs">
                <Text size="sm" weight={600}>
                  Line {index + 1}
                </Text>
                <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.lineDescription}>
                  <Textarea label="Product / description" readOnly value={line.description} minRows={2} />
                </FieldWithPrefillHint>
                <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.lineTariffNumber}>
                  <DocumentAttributePrefillField
                    label="Tariff #"
                    candidates={attributePrefillCandidates.lines[line.orderItemId]?.[wireKeys.lineTariffNumber]}
                    value={form.values.lineItems[index]?.tariffNumber ?? ""}
                    onSelect={value => form.setFieldValue(`lineItems.${index}.tariffNumber`, value)}
                  >
                    <TextInput label="Tariff #" {...form.getInputProps(`lineItems.${index}.tariffNumber`)} />
                  </DocumentAttributePrefillField>
                </FieldWithPrefillHint>
                <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.lineDuty}>
                  <DocumentAttributePrefillField
                    label="Duty"
                    candidates={attributePrefillCandidates.lines[line.orderItemId]?.[wireKeys.lineDuty]}
                    value={form.values.lineItems[index]?.duty ?? ""}
                    onSelect={value => form.setFieldValue(`lineItems.${index}.duty`, value)}
                  >
                    <TextInput label="Duty" {...form.getInputProps(`lineItems.${index}.duty`)} />
                  </DocumentAttributePrefillField>
                </FieldWithPrefillHint>
                <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.lineValueUsd}>
                  <TextInput label="Value" readOnly value={line.valueUsd} />
                </FieldWithPrefillHint>
                <FieldWithPrefillHint hint={borderCanadaPlannedPrefillHints.lineQuantityKg}>
                  <TextInput label="Quantity (kg)" {...form.getInputProps(`lineItems.${index}.quantityKg`)} />
                </FieldWithPrefillHint>
              </Stack>
            ))}
            {modalSettingsQuery.data?.allowTypedSignature ? (
              <DocumentTypedSignatureModalField
                value={form.values.typedSignature}
                onChange={typedSignature => form.setFieldValue("typedSignature", typedSignature)}
                error={typeof form.errors.typedSignature === "string" ? form.errors.typedSignature : undefined}
              />
            ) : null}
          </>
        )}
      </Stack>
    </Form>
  );
};
